/**
 * Error devuelto por la API: la petición llegó al servidor y éste respondió con
 * un código HTTP de error y el cuerpo `{ code, message, details? }` del contrato.
 *
 * Ramifica siempre sobre `code`, nunca sobre `message` (que puede cambiar).
 */
export class ApiClientError extends Error {
    name = 'ApiClientError';
    /** Código HTTP de la respuesta (400, 401, 404, 409, 500...). */
    status;
    /** Código de error estable del contrato. */
    code;
    /** Contexto adicional, normalmente el detalle campo a campo de la validación. */
    details;
    /**
     * Segundos que pide esperar la cabecera `Retry-After` (sobre todo en un 429
     * `rate_limited`), o `undefined` si la respuesta no la traía o no se pudo leer.
     */
    retryAfterSeconds;
    constructor(status, code, message, details, options) {
        super(message);
        this.status = status;
        this.code = code;
        if (details !== undefined) {
            this.details = details;
        }
        if (options?.retryAfterSeconds !== undefined) {
            this.retryAfterSeconds = options.retryAfterSeconds;
        }
        // Hermes y los targets antiguos pierden la cadena de prototipos al extender
        // `Error`; restaurarla mantiene `instanceof` fiable en React Native.
        Object.setPrototypeOf(this, ApiClientError.prototype);
    }
}
/**
 * La petición no llegó a completarse: sin conexión, DNS, CORS, timeout o
 * cualquier otro fallo de transporte. No hay respuesta HTTP que interpretar.
 */
export class NetworkError extends Error {
    name = 'NetworkError';
    constructor(message = 'No se ha podido contactar con el servidor.', options) {
        super(message);
        if (options && 'cause' in options) {
            this.cause = options.cause;
        }
        Object.setPrototypeOf(this, NetworkError.prototype);
    }
}
/**
 * Comprobación estructural: más fiable que `instanceof` cuando conviven varias
 * copias del paquete (bundles distintos en web o React Native).
 */
export function isApiClientError(value) {
    return value instanceof ApiClientError || (isErrorLike(value) && value.name === 'ApiClientError');
}
/** Comprobación estructural equivalente para {@link NetworkError}. */
export function isNetworkError(value) {
    return value instanceof NetworkError || (isErrorLike(value) && value.name === 'NetworkError');
}
function isErrorLike(value) {
    return (typeof value === 'object' &&
        value !== null &&
        typeof value.name === 'string');
}
//# sourceMappingURL=errors.js.map